import { useMemo } from 'react';
import { sortBy } from 'remeda';
import { useDecision } from '@optimizely/react-sdk';
import ProductId from '@/common/ProductId';
import {
  DealTilePositioningVariables,
  DetermineNewPriorityForDeal,
  HandleDealPriorityConflicts,
  UseDealTilePositioning,
  CheckForExistingRule,
  ResetDealsPriority,
  Deal
} from './types';
import { useDealTilePositioningAnalytics } from '../useDealTilePositioningAnalytics';

/**
 * @function determineNewPriorityForDeal
 * @description determines the new priority for a matched deal based on priority set in optimizely.
 */
const determineNewPriorityForDeal = ({ priority, deals }: DetermineNewPriorityForDeal) => {
  if (typeof priority === 'number') {
    return +priority;
  }
  if (priority === 'bottom') {
    return deals.length + 1;
  }
  if (priority === 'middle') {
    return Math.floor(deals.length / 2);
  }
  return 0;
};

/**
 * @function checkForExistingRule
 * @description determines if a deal's product code is set in optimizely to be re-prioritized.
 */
const checkForExistingRule = ({ rules, deal }: CheckForExistingRule) => rules.find((rule) => {
  const dealCodes = rule.yum;
  const { globalId: dealId } = new ProductId(deal.id);

  return dealCodes.includes(dealId);
});

/**
 * @function resetDealsPriority
 * @description reset each unmatched deal's priority so we work with properly sequenced numbers instead of
 *  deals having a high priority like 990, 1002, 1220 etc...or deals having 0 priority (yum)
 * @note - deals are already sorted before this so we are not "overriding" the initial sorting.
 */
const resetDealsPriority = ({ deals }: ResetDealsPriority) => deals.map((deal, index) => ({
  ...deal,
  priority: index
}));

/**
 * @function handleDealPriorityConflicts
 * @description handles deals having the same priority once a matched deal is re-proritized. We want
 * to keep priorites unqiue.
 */
const handleDealPriorityConflicts = ({ deals }: HandleDealPriorityConflicts) => {
  const uniquePriorities = new Set<number>();

  return deals.map((deal) => {
    let { priority } = deal;
    while (uniquePriorities.has(priority)) {
      priority += 1;
    }
    uniquePriorities.add(priority);

    return {
      ...deal,
      priority
    };
  });
};

const useDealTilePositioning = ({ deals }: UseDealTilePositioning) => {
  const [{ enabled: decisionEnabled, variables: decisionVariables }] = useDecision('cb-deal_tile_positioning', { autoUpdate: true });
  const [{ enabled: dealsOOSSortingFixEnabled }] = useDecision('bug-dtg-1700-deals-oos-sorting-fix');

  useDealTilePositioningAnalytics();

  const prioritizedDeals = useMemo(() => {
    // return deals as is if FF is not enabled
    if (!decisionEnabled) {
      return deals;
    }

    // get deal priority rules from FF
    const variables = decisionVariables as DealTilePositioningVariables;
    const dealPriorityRules = variables.deal_priority_rules?.deals;

    const dealsCopy = [...deals];
    const dealsWithRules: Deal[] = []; // contains deals that need to be prioritized based on rules from FF
    const dealsWithoutRules: Deal[] = []; // contains deals that should be prioritzed as is from BE

    if (dealsCopy?.length) {
      dealsCopy.forEach((deal) => {
        const existingRule = checkForExistingRule({
          rules: dealPriorityRules,
          deal
        });

        if (existingRule) {
          const dealCopy = { ...deal };

          const newPriority = determineNewPriorityForDeal({
            priority: existingRule.priority,
            deals: dealsCopy
          });

          dealCopy.priority = newPriority;

          dealsWithRules.push(dealCopy);
        } else {
          dealsWithoutRules.push(deal);
        }
      });

      // if there are no matching deal product codes between BE & optimizely, return deals as is since we don't need to prioritize any.
      if (dealsWithRules.length === 0) {
        return dealsWithoutRules;
      }

      const dealsWithInitialPriorities = resetDealsPriority({
        deals: dealsWithoutRules
      });
      const allDeals = [...dealsWithRules, ...dealsWithInitialPriorities];

      const updatedDeals = handleDealPriorityConflicts({
        deals: allDeals
      });

      if (dealsOOSSortingFixEnabled) {
        return sortBy(
          updatedDeals,
          (deal) => deal.outOfStock ? 1 : 0,
          (deal) => deal.priority
        );
      }
      
      return sortBy(
        updatedDeals,
        (deal) => deal.priority,
        ...[(deal: Deal) => (deal.outOfStock ?? 0)]
      );
    }

    return dealsCopy;
  }, [deals, decisionEnabled, decisionVariables, dealsOOSSortingFixEnabled]);

  return prioritizedDeals;
};

export default useDealTilePositioning;
